import React, { ReactNode } from "react";
import { Icon } from "components/icon/Icon";
import { Button } from "components/button";
import { cn } from "utils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipTrigger } from "components/ui/tooltip";
import { Text } from "components/text";
import { useTranslation } from "react-i18next";
import { ActionCardAction, ActionCardActionProps } from "./ActionCardAction";

interface ActionCardDropdownProps {
  actions: ReactNode[];
  actionOnHover?: boolean;
}

export const ActionCardDropdown: React.FC<ActionCardDropdownProps> = ({
  actions,
  actionOnHover = false,
}) => {
  const { t } = useTranslation("components");

  return (
    <DropdownMenu>
      <Tooltip>
        <TooltipTrigger asChild tabIndex={-1}>
          <DropdownMenuTrigger asChild>
            <Button
              icon="ellipsis-vertical"
              variant="link"
              className={cn(
                "flex items-center justify-center h-10 w-10 p-0",
                actionOnHover &&
                  "opacity-0 group-hover:opacity-100 transition-opacity",
              )}
              onClick={(e) => {
                e.stopPropagation();
              }}
              testId="action-card-menu"
              aria-label={t("actionCard.actions.moreActions")}
            />
          </DropdownMenuTrigger>
        </TooltipTrigger>
        <TooltipContent side="top" align="end">
          <Text variant="body">{t("actionCard.actions.moreActions")}</Text>
        </TooltipContent>
      </Tooltip>
      <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
        {actions.map((child) => {
          if (React.isValidElement(child) && child.type === ActionCardAction) {
            const props = child.props as ActionCardActionProps;
            return (
              <DropdownMenuItem
                key={`menu-action-${props.testId || props.iconName}-${props.label}`}
                onClick={(e) => {
                  if (props.onClick) {
                    e.stopPropagation();
                    props.onClick();
                  }
                }}
                disabled={props.disabled}
                className="cursor-pointer"
                data-testid={props.testId}
              >
                <Icon
                  name={props.iconName}
                  iconClassName={cn("mr-2 h-4 w-4", props.iconClassName)}
                />
                {props.label}
              </DropdownMenuItem>
            );
          }
          return null;
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
